import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { ChevronDown, Camera, X } from 'lucide-react';
import { authFetch } from '../../lib/api';
import { cn } from '../../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Module-level cache so the dropdown is instant after the first open and
// every PhotographerField on the page shares one fetch.
let _photographersCache = null;
let _photographersInflight = null;

const fetchPhotographers = async () => {
  if (_photographersCache) return _photographersCache;
  if (_photographersInflight) return _photographersInflight;
  _photographersInflight = (async () => {
    try {
      const res = await authFetch(`${API}/photographers`);
      if (!res.ok) return [];
      const data = await res.json();
      _photographersCache = data.data || [];
      return _photographersCache;
    } catch (_e) {
      return [];
    } finally {
      _photographersInflight = null;
    }
  })();
  return _photographersInflight;
};

// Allow other components to invalidate the cache when the photographer list
// is mutated elsewhere (e.g. add/edit/delete in the Photographers modal).
export const invalidatePhotographersCache = () => {
  _photographersCache = null;
};

const splitNames = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const joinNames = (arr) => arr.filter(Boolean).join(', ');

/**
 * Inline field for selecting one or more photographers for a given day.
 *
 * Behaviour:
 *   - Empty by default.
 *   - Dropdown trigger shows the comma-separated selected names (or "Select…").
 *   - Popover lists every active photographer with a checkbox, plus a
 *     filter input.
 *   - Stores the field as a comma-separated string so the rest of the
 *     codebase (exports, imports, sales_data) keeps working unchanged.
 *   - Allows free-text edit too — any name typed that isn't in the list is
 *     preserved as a "custom" entry alongside the checked names.
 *
 * Props mirror SalesField for drop-in compatibility.
 */
export const PhotographerField = ({ label, field, defaultValue, onFieldChange }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [photographers, setPhotographers] = useState(() => _photographersCache || []);
  const [value, setValue] = useState(defaultValue || '');
  const triggerRef = useRef(null);

  // Reset internal value when defaultValue changes (e.g. switching between
  // years for the same org).
  useEffect(() => {
    setValue(defaultValue || '');
  }, [defaultValue]);

  // Lazy-load the photographer list when the popover first opens.
  useEffect(() => {
    if (!open) return;
    if (photographers.length > 0) return;
    let cancelled = false;
    fetchPhotographers().then((list) => {
      if (!cancelled) setPhotographers(list);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedNames = useMemo(() => splitNames(value), [value]);
  const selectedSet = useMemo(() => new Set(selectedNames.map((n) => n.toLowerCase())), [selectedNames]);

  // Build the same phone-decoration preview that the confirmation-letter
  // merge engine produces, so operators see exactly what'll be printed.
  // Each comma-separated name is independently resolved; values that
  // already contain an 8+-digit phone-like substring are left untouched.
  // Names with no DB match pass through as-is.
  const PHONE_RE = /(?:\d[\s-]?){8,}/;
  const decoratedPreview = useMemo(() => {
    if (!selectedNames.length || !photographers.length) return '';
    const byNameLc = new Map();
    const byFirstLc = new Map();
    photographers.forEach((p) => {
      const nm = (p?.name || '').trim();
      const phone = (p?.phone || '').trim();
      if (!nm || !phone) return;
      byNameLc.set(nm.toLowerCase(), phone);
      const first = nm.split(/\s+/)[0]?.toLowerCase();
      if (first) {
        const arr = byFirstLc.get(first) || [];
        arr.push({ name: nm, phone });
        byFirstLc.set(first, arr);
      }
    });
    const decorateOne = (n) => {
      const trimmed = n.trim();
      if (!trimmed) return '';
      if (PHONE_RE.test(trimmed)) return trimmed;
      const exact = byNameLc.get(trimmed.toLowerCase());
      if (exact) return `${trimmed} (${exact})`;
      const firstTok = trimmed.split(/\s+/)[0]?.toLowerCase();
      const candidates = firstTok ? byFirstLc.get(firstTok) || [] : [];
      if (candidates.length === 1) return `${trimmed} (${candidates[0].phone})`;
      return trimmed;
    };
    return selectedNames.map(decorateOne).join(', ');
  }, [selectedNames, photographers]);

  // Lazy-load photographers as soon as the field has a saved value so we
  // can render the decorated preview even before the user opens the
  // popover. Keeps the visual hint always-correct.
  useEffect(() => {
    if (!selectedNames.length) return;
    if (photographers.length > 0) return;
    let cancelled = false;
    fetchPhotographers().then((list) => {
      if (!cancelled) setPhotographers(list);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNames.length]);

  // Names from the saved value that aren't in the canonical photographers
  // list — surfaced as a separate group so legacy free-text values are
  // visible & toggleable.
  const customNames = useMemo(() => {
    const known = new Set((photographers || []).map((p) => (p.name || '').toLowerCase()));
    return selectedNames.filter((n) => !known.has(n.toLowerCase()));
  }, [photographers, selectedNames]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return photographers;
    return photographers.filter((p) =>
      (p.name || '').toLowerCase().includes(f) ||
      (p.phone || '').includes(f)
    );
  }, [photographers, filter]);

  const togglePhotographer = (name) => {
    const has = selectedSet.has(name.toLowerCase());
    let nextSelected;
    if (has) {
      nextSelected = selectedNames.filter((n) => n.toLowerCase() !== name.toLowerCase());
    } else {
      nextSelected = [...selectedNames, name];
    }
    const joined = joinNames(nextSelected);
    setValue(joined);
    onFieldChange(field, joined);
  };

  const clearAll = () => {
    setValue('');
    onFieldChange(field, '');
  };

  // Allow direct typing for legacy compat / quick entry of off-list names.
  const handleManualChange = (e) => {
    const next = e.target.value;
    setValue(next);
    onFieldChange(field, next);
  };

  return (
    <div className="flex flex-col py-1.5 border-b border-muted/50 last:border-0">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground w-44 shrink-0">{label}:</span>
        <div className="flex items-center gap-1 w-full">
          <Input
            className="h-7 text-sm"
            value={value}
            onChange={handleManualChange}
            placeholder="-"
            data-testid={`photographer-input-${field}`}
          />
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                ref={triggerRef}
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                type="button"
                data-testid={`photographer-picker-${field}`}
                title="Pick from photographers list"
              >
                <Camera className="w-3.5 h-3.5" />
                <ChevronDown className="w-2.5 h-2.5 -ml-0.5 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0" data-testid={`photographer-popover-${field}`}>
              <div className="p-2 border-b">
                <Input
                  placeholder="Search photographers…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                  data-testid={`photographer-search-${field}`}
                />
              </div>
              <div className="max-h-64 overflow-y-auto p-1">
                {customNames.length > 0 && (
                  <div className="px-2 py-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      Custom (not in list)
                    </div>
                    {customNames.map((n) => (
                      <label key={`custom-${n}`} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer">
                        <Checkbox checked onCheckedChange={() => togglePhotographer(n)} />
                        <span className="text-sm italic text-amber-800 dark:text-amber-300">{n}</span>
                      </label>
                    ))}
                  </div>
                )}
                {filtered.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                    No photographers match
                  </div>
                ) : (
                  filtered.map((p) => {
                    const checked = selectedSet.has((p.name || '').toLowerCase());
                    return (
                      <label
                        key={p.photographer_id}
                        className={cn(
                          'flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer',
                          checked && 'bg-muted'
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => togglePhotographer(p.name)}
                          data-testid={`photographer-checkbox-${field}-${p.photographer_id}`}
                        />
                        <span className="flex-1">
                          <div className="text-sm">{p.name}</div>
                          {p.phone && <div className="text-[10px] text-muted-foreground">{p.phone}</div>}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="border-t p-1.5 flex items-center justify-between">
                <span className="text-[10px] text-muted-foreground pl-1">
                  {selectedNames.length} selected
                </span>
                <div className="flex items-center gap-1">
                  {selectedNames.length > 0 && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={clearAll} data-testid={`photographer-clear-${field}`}>
                      <X className="w-3 h-3 mr-1" />
                      Clear
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setOpen(false)}>
                    Done
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {/* Decorated preview line — visible whenever the value differs from
          its phone-decorated form, so operators can see at a glance
          whether the merged document will carry a phone number. Skipped
          when the typed value already contains digits and matches the
          decorated string exactly (avoids redundant duplication). */}
      {decoratedPreview && decoratedPreview !== value.trim() && (
        <div
          className="text-[11px] text-muted-foreground mt-0.5 ml-44 pl-1 truncate"
          data-testid={`photographer-decorated-${field}`}
          title={`In confirmation letters this will print as: ${decoratedPreview}`}
        >
          → {decoratedPreview}
        </div>
      )}
    </div>
  );
};

export default PhotographerField;
